import { useGenerateTrainerAccount, useSendTrainerInvite, useUnlinkTrainerAccount } from "api/hooks";
import { PaziaButton } from "components/atoms";
import { useToaster } from "utils";

interface TrainerAccountPanelProps {
  trainerId: number;
  email: string | null;
  userId: number | null;
  userEmail: string | null;
  onAccountGenerated: () => void;
}

export const TrainerAccountPanel = ({
  trainerId,
  email,
  userId,
  userEmail,
  onAccountGenerated,
}: TrainerAccountPanelProps) => {
  // Zaproszenie i reset hasla ida na adres konta, nie na email z karty trenera
  const accountEmailMismatch = !!userId && !!userEmail && userEmail !== email;
  const { showSuccess, showError } = useToaster();
  const generateAccountMutation = useGenerateTrainerAccount(trainerId);
  const sendInviteMutation = useSendTrainerInvite(trainerId);
  const unlinkAccountMutation = useUnlinkTrainerAccount(trainerId);

  const handleGenerateAccount = async () => {
    try {
      await generateAccountMutation.mutateAsync();
      showSuccess("Konto trenera zostało utworzone");
      onAccountGenerated();
    } catch {
      showError("Nie udało się utworzyć konta trenera");
    }
  };

  const handleSendInvite = async () => {
    try {
      await sendInviteMutation.mutateAsync();
      showSuccess("Zaproszenie do panelu trenera zostało wysłane");
    } catch {
      showError("Nie udało się wysłać zaproszenia");
    }
  };

  const handleUnlinkAccount = async () => {
    if (
      !window.confirm(
        "Czy na pewno chcesz odpiąć konto od tego trenera? Konto straci dostęp do panelu trenera.",
      )
    ) {
      return;
    }

    try {
      await unlinkAccountMutation.mutateAsync();
      showSuccess("Konto zostało odpięte od trenera");
      onAccountGenerated();
    } catch {
      showError("Nie udało się odpiąć konta od trenera");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-semibold text-slate-800 dark:text-slate-100">Konto w panelu trenera</h2>

      {!email && (
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Uzupełnij adres e-mail trenera, aby móc utworzyć konto do panelu trenera.
        </div>
      )}

      {email && (
        <>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            {userId
              ? "Trener ma konto do logowania w panelu trenera."
              : "Trener nie ma jeszcze konta do logowania w panelu trenera."}
          </div>
          {userId && userEmail && (
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Login konta (na ten adres wysyłane jest zaproszenie):{" "}
              <span className="font-semibold text-slate-800 dark:text-slate-100">{userEmail}</span>
            </div>
          )}
          {accountEmailMismatch && (
            <div className="text-sm text-amber-600 dark:text-amber-400">
              Adres konta różni się od adresu e-mail trenera ({email}). Zapisz ponownie dane trenera, aby
              zsynchronizować adres konta, zanim wyślesz zaproszenie.
            </div>
          )}
          <div className="flex gap-2">
            {!userId && (
              <PaziaButton
                title="Wygeneruj konto"
                type="primary"
                btnType="button"
                disabled={generateAccountMutation.isPending}
                onClick={handleGenerateAccount}
              />
            )}
            {userId && (
              <PaziaButton
                title="Wyślij zaproszenie do panelu"
                type="tertiary"
                btnType="button"
                disabled={sendInviteMutation.isPending}
                onClick={handleSendInvite}
              />
            )}
            {userId && (
              <PaziaButton
                title="Odepnij konto"
                type="danger"
                btnType="button"
                disabled={unlinkAccountMutation.isPending}
                onClick={handleUnlinkAccount}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
